import { useState, useEffect } from "react";
import { getOrdersByUser, getReadingHistory, getUserAnalytics, getWishlist, getRewardPoints } from "../api/productApi";

export default function useAnalytics(selectedMember, token) {
    const [selected, setSelected] = useState("Reading History");
    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [readingHistory, setReadingHistory] = useState([]);
    const [readingTotal, setReadingTotal] = useState(0);
    const [userAnalytics, setUserAnalytics] = useState(null);
    const [wishlist, setWishlist] = useState([]);
    const [rewardPoints, setRewardPoints] = useState(null);
    const [cache, setCache] = useState({ orders: null, reading: null, analytics: null, wishlist: null, rewardPoints: null });
    const [toast, setToast] = useState("");
    const [expandedOrders, setExpandedOrders] = useState({});

    // Reset state on active member change
    useEffect(() => {
        setOrders([]);
        setReadingHistory([]);
        setReadingTotal(0);
        setUserAnalytics(null);
        setWishlist([]);
        setRewardPoints(null);
        setCache({ orders: null, reading: null, analytics: null, wishlist: null, rewardPoints: null });
        setExpandedOrders({});
    }, [selectedMember?.id]);

    useEffect(() => {
        if (!selectedMember?.id || !token) {
            return;
        }

        const fetchOrderHistory = async () => {
            if (cache.orders) {
                setOrders(cache.orders);
                return;
            }
            setLoading(true);
            setOrders([]);
            try {
                const res = await getOrdersByUser({ token, userId: selectedMember.id });
                const resData = res?.data || res || [];
                setOrders(resData);
                setCache(prev => ({ ...prev, orders: resData }));
            } catch (err) {
                console.error("Failed to load Order History", err);
                showToast("Failed to load Order History. Please try again.");
                setOrders([]);
            } finally {
                setLoading(false);
            }
        };

        const fetchReadingHistory = async () => {
            if (cache.reading) {
                setReadingHistory(cache.reading.data);
                setReadingTotal(cache.reading.total);
                return;
            }
            setLoading(true);
            setReadingHistory([]);
            setReadingTotal(0);
            try {
                const res = await getReadingHistory({ token, userId: selectedMember.id });
                const historyData = res?.data || [];
                const totalCount = res?.total || historyData.length || 0;
                setReadingHistory(historyData);
                setReadingTotal(totalCount);
                setCache(prev => ({ ...prev, reading: { data: historyData, total: totalCount } }));
            } catch (err) {
                console.error("Failed to load Reading History", err);
                showToast("Failed to load Reading History. Please try again.");
                setReadingHistory([]);
                setReadingTotal(0);
            } finally {
                setLoading(false);
            }
        };

        const fetchUserAnalytics = async () => {
            if (cache.analytics) {
                setUserAnalytics(cache.analytics);
                return;
            }
            setLoading(true);
            setUserAnalytics(null);
            try {
                const res = await getUserAnalytics({ token, userId: selectedMember.id });
                const analyticsData = res?.data || res;
                setUserAnalytics(analyticsData);
                setCache(prev => ({ ...prev, analytics: analyticsData }));
            } catch (err) {
                console.error("Failed to load User Analytics", err);
                showToast(err.message || "Failed to load User Analytics. Please try again.");
                setUserAnalytics(null);
            } finally {
                setLoading(false);
            }
        };

        const fetchWishlist = async () => {
            if (cache.wishlist) {
                setWishlist(cache.wishlist);
                return;
            }
            setLoading(true);
            setWishlist([]);
            try {
                const res = await getWishlist({ token, userId: selectedMember.id });
                const wishlistData = res?.data || res || [];
                let normalizedData = [];
                if (Array.isArray(wishlistData)) {
                    normalizedData = wishlistData;
                } else if (wishlistData && typeof wishlistData === "object") {
                    if (wishlistData.product_id) {
                        normalizedData = [wishlistData];
                    } else {
                        normalizedData = [];
                    }
                }
                setWishlist(normalizedData);
                setCache(prev => ({ ...prev, wishlist: normalizedData }));
            } catch (err) {
                console.error("Failed to load Wishlist", err);
                showToast(err.message || "Failed to load Wishlist. Please try again.");
                setWishlist([]);
            } finally {
                setLoading(false);
            }
        };

        const fetchRewardPoints = async () => {
            if (cache.rewardPoints) {
                setRewardPoints(cache.rewardPoints);
                return;
            }
            setLoading(true);
            setRewardPoints(null);
            try {
                const res = await getRewardPoints({ token, userId: selectedMember.id });
                const pointsData = res?.data || res;
                setRewardPoints(pointsData);
                setCache(prev => ({ ...prev, rewardPoints: pointsData }));
            } catch (err) {
                console.error("Failed to load Reward Points", err);
                showToast(err.message || "Failed to load Reward Points. Please try again.");
                setRewardPoints(null);
            } finally {
                setLoading(false);
            }
        };

        if (selected === "Order History") {
            fetchOrderHistory();
        } else if (selected === "Reading History") {
            fetchReadingHistory();
        } else if (selected === "User Analytics") {
            fetchUserAnalytics();
        } else if (selected === "Wishlist") {
            fetchWishlist();
        } else if (selected === "Reward Points") {
            fetchRewardPoints();
        }
    }, [selected, selectedMember?.id, token, cache]);

    const showToast = (message) => {
        setToast(message);
        setTimeout(() => setToast(""), 3000);
    };

    const toggleOrderExpand = (orderId) => {
        setExpandedOrders(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    return {
        selected,
        setSelected,
        loading,
        orders,
        readingHistory,
        readingTotal,
        userAnalytics,
        wishlist,
        rewardPoints,
        toast,
        expandedOrders,
        toggleOrderExpand,
    };
}
